import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, Download } from "lucide-react";
import { Button } from "./ui/button";
import { STATS, HERO_IMAGES, PARTNERS } from "../mock";
import { Reveal } from "./Reveal";

const Hero = () => {
  const ctaRef = useRef(null);
  const handleMove = (e) => {
    const el = ctaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--x", `${e.clientX - r.left}px`);
    el.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  return (
    <section className="relative overflow-hidden bg-white">
      <div className="absolute top-16 -right-32 w-96 h-96 bg-[#6BBE32]/10 rounded-full blur-3xl"/>
      <div className="absolute -bottom-20 -left-32 w-96 h-96 bg-[#15348C]/5 rounded-full blur-3xl"/>

      <div className="relative max-w-7xl mx-auto px-4 pt-10 pb-14 lg:pt-16 lg:pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <Reveal variant="up">
            <div className="inline-flex items-center gap-2 bg-white border border-[#6BBE32]/30 shadow-sm px-3 py-1.5 rounded-full text-xs font-semibold text-[#4F9622] mb-5 sg-pulse">
              <Sparkles size={14}/> #1 Authorized Training &amp; Certification Partner
            </div>
          </Reveal>
          <Reveal variant="up" delay={100}>
            <h1 className="text-[#15348C] tracking-tight">
              Future-Ready <span className="text-[#F57C20]">AI &amp; Cloud Skills</span><br/>
              Built for Today’s Enterprise
            </h1>
          </Reveal>
          <Reveal variant="up" delay={200}>
            <p className="mt-5 text-gray-600 max-w-xl leading-relaxed" style={{fontSize:"1.075rem"}}>
              <strong className="text-[#15348C]">250+ enterprise clients</strong> trust SkillsGain to upskill <strong className="text-[#15348C]">4,000+ professionals</strong> across <strong className="text-[#15348C]">30+ emerging technology domains</strong> and <strong className="text-[#15348C]">200+ corporate learning programs</strong>.
            </p>
          </Reveal>

          <Reveal variant="up" delay={280}>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/offerings/all">
                <Button ref={ctaRef} onMouseMove={handleMove}
                  className="sg-btn-glow bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/30">
                  Explore All Offerings <ArrowRight size={17} className="ml-1"/>
                </Button>
              </Link>
              <a href="/SkillsGain-Course-Catalogue.pdf" download data-testid="hero-download-catalogue">
                <Button className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#F57C20]/30">
                  <Download size={17} className="mr-1"/> Download Catalogue
                </Button>
              </a>
            </div>
          </Reveal>

          <Reveal variant="up" delay={360}>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
              {["AWS Authorized", "Microsoft Partner", "Google Cloud Partner", "Red Hat ATP", "Salesforce Partner"].map(t => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[#6BBE32]"/>{t}</span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Hero visual: two stacked enterprise-training images */}
        <Reveal variant="right" delay={120}>
          <div className="relative" data-testid="hero-visual">
            <div className="grid grid-cols-1 gap-5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
                <img
                  src={HERO_IMAGES[0]}
                  alt="Corporate training session in progress"
                  className="w-full h-[230px] lg:h-[260px] object-cover"
                  data-testid="hero-image-1"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#15348C]/40 via-transparent to-transparent" />
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100 ml-auto w-[88%]">
                <img
                  src={HERO_IMAGES[1]}
                  alt="Collaborative learning workshop"
                  className="w-full h-[200px] lg:h-[230px] object-cover"
                  data-testid="hero-image-2"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#15348C]/40 via-transparent to-transparent" />
              </div>
            </div>
            {/* Mini accent badge */}
            <div className="absolute -top-3 -left-3 bg-[#15348C] text-white rounded-xl shadow-2xl px-4 py-2.5 border-2 border-white">
              <div className="text-xl font-bold text-[#8BD835] leading-none" style={{ fontFamily: "Times New Roman, serif" }}>250+</div>
              <div className="text-[10px] text-gray-200 leading-tight mt-1">Enterprise<br />clients</div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Partner logos strip */}
      <Reveal>
        <div className="relative max-w-7xl mx-auto px-4 pt-10 pb-8">
          <div className="text-center text-xs font-bold tracking-widest text-gray-400 mb-5">CERTIFICATION PARTNERSHIPS</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 opacity-80">
            {PARTNERS.slice(0, 8).map(p => (
              p.logo
                ? <img key={p.name} src={p.logo} alt={p.name} className="h-8 max-w-[110px] object-contain"/>
                : <span key={p.name} className="text-[#15348C] font-bold text-sm">{p.name}</span>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal>
        <div className="relative max-w-7xl mx-auto px-4 pb-10">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            {STATS.map((s) => (
              <div key={s.label} className="p-6 text-center group">
                <div className="text-3xl lg:text-4xl font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{fontFamily:'Times New Roman, serif'}}>{s.value}</div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default Hero;
